/**
 * XSS Protection Utilities using DOMPurify
 * Sanitizes user input to prevent XSS attacks
 */

import DOMPurify from 'isomorphic-dompurify';

/**
 * Sanitize HTML content
 * @param {string} dirty - Potentially unsafe HTML string
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHTML = (dirty) => {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'a'
    ],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
    ALLOW_DATA_ATTR: false
  });
};

/**
 * Sanitize plain text (removes all HTML)
 * @param {string} text - Text that may contain HTML
 * @returns {string} - Plain text with HTML removed
 */
export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  // Remove all HTML tags
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
};

/**
 * Sanitize URL
 * @param {string} url - Potentially unsafe URL
 * @returns {string} - Sanitized URL or empty string
 */
export const sanitizeURL = (url) => {
  if (!url || typeof url !== 'string') {
    return '';
  }
  // Only allow http, https, and relative URLs
  const trimmed = url.trim();
  if (trimmed.match(/^(https?:\/\/|\/|#)/i)) {
    return DOMPurify.sanitize(trimmed);
  }
  return '';
};

/**
 * Sanitize user input for form fields
 * Removes potentially dangerous characters and HTML
 * @param {string} input - User input string
 * @returns {string} - Sanitized input
 */
export const sanitizeInput = (input) => {
  if (!input || typeof input !== 'string') {
    return '';
  }
  // Remove HTML tags and dangerous characters
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] })
    .replace(/[<>]/g, '') // Remove any remaining angle brackets
    .trim();
};
